/**
 * Window-exposed test bridge for the Three.js renderer.
 *
 * # Why this exists (resolves edge case #17)
 *
 * Today's Playwright specs query the DOM via `[data-entity-id]` SVG
 * selectors. After the renderer switch the canvas has no per-entity
 * DOM nodes, so those selectors stop working. Rather than rewrite
 * every spec around `<canvas>` raster sampling, we expose a small
 * imperative API on `window.__edit_dwg_scene` so specs can query the
 * scene by id, world coords or selection state.
 *
 * The bridge is dev-only and gated on `import.meta.env.DEV` plus the
 * forthcoming `editor.exposeSceneApi` sysvar. Production builds have
 * the API tree-shaken away — `installSceneTestBridge` is the single
 * entry point and the surface lives only inside its closure.
 *
 * PR-A ships the skeleton: every method returns a stub value so the
 * bridge is callable from the moment the canvas mounts, but the
 * underlying entity / selection state isn't wired yet. PR-B/C/G fill
 * in the real implementations.
 */

import { geometryToLineVertices } from "./batchedLayer";
import { pick } from "./raycast/pick";
import { selectInWindow, type SelectionMode } from "./raycast/windowSelect";
import { loadSdfAtlas } from "./text/sdfAtlasLoader";
import type { RendererHandle } from "./types";
import {
  applyOffset,
  applyOffsetToBounds,
  unapplyOffset,
} from "./coordinateOrigin";

/**
 * Public test API. Each method MUST be safe to call before any DXF
 * is loaded — methods return `null` / `0` / empty arrays in that
 * state, never throw.
 */
export interface SceneTestApi {
  /**
   * Pick the topmost entity at the given client-space pixel.
   *
   * MODEL-SPACE contract: the query is projected with the MAIN camera
   * only. Geometry displayed through a paper-space layout VIEWPORT is
   * not routed through that viewport's model transform the way
   * production's own click path is (`resolveActiveInputSpace()` in
   * `StagePointerHandlers.ts`), so a click aimed inside a VP can miss
   * or resolve the wrong id. See the implementation's "KNOWN GAP 2".
   */
  entityIdAt(clientX: number, clientY: number): string | null;
  /** Total number of entities currently registered with the scene. */
  entityCount(): number;
  /**
   * N2 (S10E-ANNOTATIVE-STUB-REPROJECT) — run the REAL production window/
   * crossing select (`raycast/windowSelect.ts` `selectInWindow`) directly
   * against the live spatial index. Lets a scenario prove spatial-index
   * stub-bounds correctness without needing to simulate a pixel-perfect
   * on-screen drag gesture (local-functional-check.mjs has no raw
   * mousedown/up action).
   *
   * INPUT FRAME — raw AutoCAD-WORLD, the SAME public convention every other
   * world-coordinate member of this bridge uses (`entityIdAt`'s projected
   * point, `entityWorldBounds`'s return, `cameraWorldRect()`,
   * `cameraPosition()`: all offset re-added, i.e. document coordinates, NOT
   * client-space pixels and NOT the renderer's offset-world frame). The
   * implementation converts to the index's frame itself — see there.
   *
   * SIGN CONVENTION — `top = MIN world Y`, `bottom = MAX world Y`, matching
   * `entityWorldBounds()` and `spatialIndex.intersect()`'s direct
   * `minY: rect.top` passthrough. This is NOT `cameraWorldRect()`'s
   * screen-top sense (whose `top` is the frustum's MAX Y), so a
   * `cameraWorldRect()` rect must not be reused here verbatim.
   */
  windowSelectWorldIds(
    rect: { left: number; top: number; right: number; bottom: number },
    mode: "window" | "crossing",
  ): string[];
  /**
   * SL-15 render-elevation discriminator. Resolves the entity to the SAME
   * `RenderableEntity` the bulk-load pass fed to the GPU line buckets
   * (`SceneBulkLoad` wires the spatial-index resolver to that pass's
   * `renderableById` map), then runs the EXACT live tessellation
   * (`geometryToLineVertices` — the function `batchedLayer.addEntity` uses to
   * fill the vertex buffer) and returns the min / max z of the emitted vertex
   * stream, plus the vertex `count`.
   *
   * OUTPUT FRAME — the renderer's OFFSET-world, not the raw AutoCAD-world the
   * bridge's other coordinate members return: these are literal GPU buffer
   * values. Only `z` is exposed and the locked origin's `z` is always 0
   * (`createCoordinateOriginAccumulator` returns `z: 0`;
   * `SceneBulkLoad.ts`'s fallback locks `{0,0,0}`), so the two frames
   * coincide here in practice. Same caveat as
   * {@link SceneTestApi.entityRenderedMajorAxisEnd}, which exposes x/y too.
   *
   * This reads the z of the RENDERED / tessellated geometry, NOT the document
   * field: a renderer that flattened an elevated ellipse's `center.z` to 0
   * would paint the same top-down pixels but return `{min:0,max:0}` here, so a
   * scenario can assert the render honours the elevation. Returns `null` when
   * the id is unknown (resolver miss) or the entity kind emits no line-bucket
   * vertices (text / hatch / point / etc.).
   */
  entityRenderedVertexZRange(
    id: string,
  ): { min: number; max: number; count: number } | null;
  /**
   * SL-15 F-C tilt discriminator. Returns the FIRST tessellated vertex of the
   * entity's RENDERED line stream. For an ELLIPSE that is the major-axis ENDPOINT
   * (the sampler starts at `start` param; a full ellipse starts at the major
   * axis), i.e. `offsetCenter + majorAxis` in the true 3D plane. A pre-fix flat
   * sampler dropped the major-axis z (endpoint z ≡ center.z) AND shortened the
   * major to its XY projection; the 3D sampler returns the true tilted endpoint,
   * so a scenario can assert the render honours a `majorAxisEndpoint.z ≠ 0` tilt.
   * Returns `null` when the id is unknown or the kind emits no line vertices.
   *
   * OUTPUT FRAME — OFFSET-world, as the `offsetCenter` above says: a raw GPU
   * vertex. This is the one coordinate-bearing member of this bridge that is
   * NOT raw AutoCAD-world, deliberately, because its whole purpose is to read
   * what the tessellator actually emitted. Under a non-zero locked origin its
   * x/y differ from the document coordinate by the origin; `z` does not (the
   * locked origin's z is always 0). Callers wanting the document frame should
   * use `entityWorldBounds`, or add the origin back themselves.
   */
  entityRenderedMajorAxisEnd(
    id: string,
  ): { x: number; y: number; z: number } | null;
  /**
   * Bounding box of one entity in client-space pixels — camera-dependent,
   * exactly what `getBoundingClientRect()` on the entity's rendered pixels
   * would report. Goes through the live `worldFromClient`/`clientFromWorld`
   * camera+canvas projection (see `scene/useStageRendererHandle.ts`
   * `buildProjection`), so the SAME entity reports DIFFERENT numbers at
   * different zoom/pan — use `entityWorldBounds` instead for a
   * camera-invariant reading (e.g. comparing captures taken across a ZOOM
   * command, or against known world-space fixture coordinates).
   *
   * MODEL-SPACE contract, same limit as `entityIdAt`: bounds are
   * projected with the MAIN camera only, so for geometry painted through
   * a paper-space layout VIEWPORT this reports where it would sit in
   * paper coords, not the pixels the viewport actually draws it at.
   */
  entityBounds(
    id: string,
  ): { left: number; top: number; right: number; bottom: number } | null;
  /**
   * Bounding box of one entity in raw AutoCAD-WORLD units — reads
   * `spatialIndex.getBounds(id)` directly with NO camera/canvas
   * projection, so it is IDENTICAL across any zoom/pan and safe to compare
   * across a ZOOM command or reuse across scenario stages. Convention:
   * `top = MIN world Y`, `bottom = MAX world Y` (NOT `cameraWorldRect()`'s
   * screen-top sense, since this never touches the camera). Resolver-
   * independent (bounds live in the index intrinsically — see
   * `SpatialIndex.getBounds`), so it also works for spatial-index stubs
   * that `entityIdAt`/`windowSelectWorldIds` cannot resolve to a full
   * renderable. Use `entityBounds` instead for real on-screen pixels.
   *
   * MODEL-SPACE contract, the same limit `entityIdAt` / `entityBounds`
   * carry. "AutoCAD-world" here means the frame the SHARED SPATIAL INDEX
   * holds for this id. In layout mode `SceneBulkLoad.ts` also appends
   * `modelSpaceEntities`, and those entries are indexed as the active
   * paper-space VIEWPORT maps them — so for an entity displayed THROUGH a
   * viewport this returns that viewport-mapped paper-space box, not the
   * entity's model-space WCS bounds. Do not treat this as a WCS oracle in
   * a layout scenario; in plain model space (every current consumer) the
   * two coincide and the reading is exact.
   */
  entityWorldBounds(
    id: string,
  ): { left: number; top: number; right: number; bottom: number } | null;
  /** Whether one entity is currently visible (layer not hidden / off). */
  entityVisible(id: string): boolean;
  /** Currently selected entity ids. */
  selectionIds(): string[];
  /**
   * PR-C: programmatically set the selection from a test. Wires
   * through to the editor reducer; UI / rendering follow the normal
   * post-dispatch flow.
   */
  setSelection(ids: string[]): void;
  /** Camera frustum expressed as AutoCAD-world rect (offset re-added). */
  cameraWorldRect(): {
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
  /**
   * Current orthographic zoom factor (1.0 = no zoom). Used by
   * `scripts/live-smoke.mjs` `probeZoom` and
   * `scripts/local-functional-check.mjs` wheel-zoom scenarios as the
   * canonical source of camera state — direct read of `ortho.zoom` is
   * a stable contract; pixel-fingerprint diff is advisory only.
   *
   * Returns 0 if the bridge isn't wired to a real camera (PR-A stub
   * default). Callers MUST treat 0 as "unavailable", not "zoomed out".
   */
  cameraZoom(): number;
  /**
   * Camera centre in AutoCAD world coords (offset re-added). Used by
   * pan-regression probes and snap/grip tests that need to know which
   * part of the drawing the user is currently looking at.
   *
   * Returns `{x:0,y:0}` if the bridge isn't wired (PR-A stub default).
   */
  cameraPosition(): { x: number; y: number };
  /** Promise resolving to a PNG dataURL of the canvas. */
  snapshotPng(): Promise<string>;
  /** Resolves on the next animation frame. */
  waitForFrame(): Promise<void>;
  /**
   * PR-E1: drive the SDF atlas loader from a test. Resolves once the
   * pre-built atlas pair (`/sdf-atlases/{fontName}.{png,json}`) is
   * fetched + parsed + uploaded to a `THREE.Texture`. Rejects with
   * `MissingAtlasError` if either side is unavailable. The atlas is
   * cached per-fontName for the page lifetime — repeat calls resolve
   * immediately.
   *
   * PR-E2 wires this into the `<MText>` adapter so Playwright specs
   * can `await __edit_dwg_scene.preloadFontAtlas("Roboto-Regular")`
   * before asserting on text geometry.
   */
  preloadFontAtlas(fontName: string): Promise<void>;
}

/**
 * Minimal store contract the bridge consumes — declared narrow on
 * purpose so the test surface doesn't import zustand types directly.
 * PR-C extended the contract with `setSelection` so tests can drive
 * the selection reducer programmatically.
 */
export interface SceneBridgeStore {
  /** Currently selected entity ids. */
  getSelectionIds: () => string[];
  /** Whether the entity is rendered (layer ON, visible flag true). */
  isEntityVisible: (id: string) => boolean;
  /**
   * PR-C: dispatch a `shell/select-entities` (or `shell/clear-selection`
   * for an empty array) so the test bridge can drive the selection
   * reducer the same way the click handler does. Optional in PR-A's
   * stub store — tests / Scene3DStage wire the real one.
   */
  setSelection?: (ids: string[]) => void;
}

/**
 * Optional projection helpers the bridge needs to translate between
 * client-space pixels (the only thing Playwright passes) and the
 * AutoCAD-world coords the spatial index understands. Wired by
 * Scene3DStage when it mounts; null in PR-A's empty-canvas case
 * (bridge falls back to identity projection).
 */
export interface SceneBridgeProjection {
  /** Project client (CSS px) → world (AutoCAD-world, offset re-added). */
  worldFromClient: (
    clientX: number,
    clientY: number,
  ) => { x: number; y: number };
  /** Inverse projection. */
  clientFromWorld: (world: { x: number; y: number }) => {
    x: number;
    y: number;
  };
  /** AutoCAD-world rect bounds for the camera's frustum. */
  cameraWorldRect: () => {
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
  /**
   * Current orthographic zoom factor. Wired through to the underlying
   * `THREE.OrthographicCamera.zoom`. Returns 0 if the projection
   * helper has no camera reference (stub default).
   */
  cameraZoom: () => number;
  /**
   * Camera centre in AutoCAD world coords (offset re-added). Returns
   * `{x:0,y:0}` if the projection helper has no camera reference
   * (stub default).
   */
  cameraPosition: () => { x: number; y: number };
  /**
   * Tolerance (in world units) the bridge passes to spatialIndex.pick.
   * Defaults to a small fixed value if the projection helper isn't
   * provided.
   */
  worldTolerance: () => number;
}

/**
 * No-op store that returns empty selection / always-visible. Lets
 * PR-A mount the bridge without forcing a real wire-up to editorStore
 * — that wiring lands alongside selection plumbing in PR-C.
 */
export const stubSceneBridgeStore: SceneBridgeStore = {
  getSelectionIds: () => [],
  isEntityVisible: () => true,
  setSelection: () => {
    /* no-op stub */
  },
};

const BRIDGE_PROPERTY_NAME = "__edit_dwg_scene" as const;

/**
 * Dev-only / opt-in detection. We honour `import.meta.env.DEV` plus
 * a runtime sysvar override; production builds with neither path
 * active leave the bridge un-attached.
 *
 * The sysvar piece lives on `window.__edit_dwg_scene_force_install`
 * for now (set by the e2e test harness before page load); the long
 * term home is the `editor.exposeSceneApi` flag (Q3.A follow-up).
 */
function isBridgeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  // Vite injects import.meta.env.DEV on dev builds. Defensive guard
  // so this module is also safe under Jest / jsdom paths that don't
  // run vite's transform.
  const importMetaEnv = (import.meta as { env?: { DEV?: boolean } }).env;
  if (importMetaEnv?.DEV) return true;
  const forced = (window as unknown as Record<string, unknown>)[
    "__edit_dwg_scene_force_install"
  ];
  return forced === true;
}

/**
 * Mounts the bridge onto `window.__edit_dwg_scene`. Idempotent —
 * calling twice replaces the previous handle so React StrictMode
 * double-invokes don't leave stale state behind.
 *
 * Returns a teardown that detaches the bridge. Callers (Scene3DStage)
 * invoke it on unmount.
 */
export function installSceneTestBridge(
  handle: RendererHandle,
  store: SceneBridgeStore = stubSceneBridgeStore,
  projection?: SceneBridgeProjection,
): () => void {
  if (!isBridgeEnabled()) {
    return () => {
      /* no-op */
    };
  }
  if (typeof window === "undefined") {
    return () => {
      /* no-op */
    };
  }

  const projectionOrIdentity: SceneBridgeProjection = projection ?? {
    worldFromClient: (clientX, clientY) => ({ x: clientX, y: clientY }),
    clientFromWorld: (world) => ({ x: world.x, y: world.y }),
    cameraWorldRect: () => ({ left: 0, top: 0, right: 0, bottom: 0 }),
    cameraZoom: () => 0,
    cameraPosition: () => ({ x: 0, y: 0 }),
    worldTolerance: () => 1,
  };

  const api: SceneTestApi = {
    entityIdAt: (clientX: number, clientY: number): string | null => {
      // PR-C: analytic raycaster pick (rbush prefilter → per-kind
      // distance via `scene3d/raycast/distance.ts`). Replaces the
      // bbox-area heuristic of the PR-B coarse pick.
      //
      // #589 Codex review — `worldFromClient` returns AutoCAD-world
      // (coordinateOrigin re-added), but `spatialIndex` stores bounds in
      // the renderer's OFFSET-world frame (every geometry adapter calls
      // `applyOffset` on absolute-position inputs — see
      // `sceneGeometryBuilders.ts`), which is also exactly the frame
      // production's own click handler (`StagePointerHandlers.ts`'s local
      // `clientToWorld`, which never calls `unapplyOffset` either) queries
      // `pick()`/`intersect()` with. `applyOffset` here undoes
      // `worldFromClient`'s `unapplyOffset` — mathematically an exact
      // inverse — landing back on that same render-space point rather than
      // the public AutoCAD-world one, so the bridge queries the index in
      // the same frame a real click does.
      //
      // FORMER GAP (issue #599), now CLOSED — do not re-derive a pick-path
      // frame branch from this code. The cache-direct (>5000-entity) import
      // path used to bulk-load RAW/AutoCAD-world stubs while every query
      // stayed offset-world, so a real click on such an import with a
      // locked `coordinateOrigin` missed by the whole origin and this
      // bridge deliberately mirrored that miss. The spatial index is now
      // ALWAYS offset-world on BOTH bulk-load branches: the raw
      // `EntityMetadataIndex` box is converted at the single seam in
      // `state/spatialBuilder.ts buildSpatialRecordsForSpaces`, and
      // `scene/SceneBulkLoad.ts`'s cache-direct branch declares
      // `setBoundsFrame("offset-world")` like the legacy one. So the
      // unconditional `applyOffset` below is correct for every import path,
      // and `StagePointerHandlers.ts` needs no cache-direct special case —
      // which is why it still has none. The pinning property is
      // `scene3d/__tests__/coordinateOriginInvariance.metamorphic.test.ts`.
      // KNOWN GAP 2 — PAPER-SPACE VIEWPORT REDIRECT, likewise not
      // introduced here and NARROWED (not widened) by this fix. When a
      // layout is active, `SceneBulkLoad.ts` appends `modelSpaceEntities`
      // to this same shared index, and production's click path
      // (`StagePointerHandlers.ts`) first routes the paper point through
      // `resolveActiveInputSpace()` — mapping it into the VP's MODEL
      // coords and re-sizing the aperture by the VP's `pixelsPerWorld`
      // — before querying the index. This bridge has no
      // `viewportRedirectRef` / `activeSpaceId` / `paperPixelsPerWorld`
      // threaded into it, so it queries in PAPER coords: a click on
      // geometry shown THROUGH an unlocked viewport can miss or resolve
      // the wrong id. Plain model space (every current bridge consumer)
      // is unaffected. Before this PR `worldFromClient` was an identity
      // stub, so this path was wrong for EVERY camera, viewport or not;
      // wiring the real main-camera projection makes model space exact
      // and leaves only the VP-frame case outstanding. Closing it means
      // threading the redirect state into `SceneBridgeProjection` — a
      // separate slice, deliberately out of this fix's scope rather than
      // half-wired here.
      const world = projectionOrIdentity.worldFromClient(clientX, clientY);
      const renderSpace = applyOffset({ x: world.x, y: world.y, z: 0 });
      const tolerance = projectionOrIdentity.worldTolerance();
      return pick(renderSpace, tolerance, handle.spatialIndex);
    },
    entityCount: (): number => handle.spatialIndex.size(),
    windowSelectWorldIds: (
      rect: { left: number; top: number; right: number; bottom: number },
      mode: "window" | "crossing",
    ): string[] => {
      // #794 Codex round 7 — the rect arrives in RAW/AutoCAD-world (this
      // method's documented public contract, and what
      // `docs/ui-reference/annotative-stub-reproject/scenario.json` actually
      // passes: a window around the fixture's DOCUMENT coordinates
      // (20000,20000)-(20250,20250)). `selectInWindow` queries the spatial
      // index in the renderer's OFFSET-world frame on BOTH phases — the
      // rbush `intersect()` broad phase against index bounds, and the
      // analytic narrow phase against resolved `RenderableEntity.geometry`,
      // which every adapter emits post-`applyOffset`. So the rect must cross
      // the SAME seam `entityIdAt` already crosses for its projected point.
      //
      // This became load-bearing in THIS PR: the spatial index used to be
      // raw-world on the cache-direct branch, so passing the raw rect
      // through happened to line up there. `state/spatialBuilder.ts
      // buildSpatialRecordsForSpaces` now converts at the one seam and both
      // bulk-load branches declare `"offset-world"`, which left this the one
      // bridge entry point still handing the index a document-frame value —
      // under a non-zero locked origin the >5000-entity cache-direct probe
      // would have missed by the whole origin while `entityIdAt` hit, i.e.
      // the two halves of one bridge disagreeing about their input frame.
      //
      // `applyOffsetToBounds` (not an inline `- origin`) is the ONE home for
      // "document-frame box → render-frame box", shared with the stub
      // builder and the viewport cull query. It is a pure componentwise
      // translation, so it preserves the `top = minY` / `bottom = maxY` sign
      // convention above and cannot flip a rect.
      //
      // No origin locked (every current fixture — `createCoordinateOriginAccumulator`
      // rounds the content-bbox centre to the nearest 1e6) → identity, so no
      // existing scenario moves.
      const offset = applyOffsetToBounds({
        minX: rect.left,
        minY: rect.top,
        maxX: rect.right,
        maxY: rect.bottom,
      });
      return selectInWindow(
        {
          left: offset.minX,
          top: offset.minY,
          right: offset.maxX,
          bottom: offset.maxY,
        },
        mode as SelectionMode,
        handle.spatialIndex,
      );
    },
    entityRenderedVertexZRange: (
      id: string,
    ): { min: number; max: number; count: number } | null => {
      // Resolve to the exact renderable the bulk-load pass rendered, then
      // reproduce its GPU line-vertex stream with the live tessellator. The
      // vertex layout is [x,y,z, x,y,z, …] so z lives at every 3rd slot.
      const renderable = handle.spatialIndex.resolve(id);
      if (!renderable) return null;
      const verts = geometryToLineVertices(renderable.geometry);
      if (verts.length < 3) return null;
      let min = Infinity;
      let max = -Infinity;
      let count = 0;
      for (let i = 2; i < verts.length; i += 3) {
        const z = verts[i];
        if (typeof z !== "number" || !Number.isFinite(z)) continue;
        if (z < min) min = z;
        if (z > max) max = z;
        count++;
      }
      if (count === 0) return null;
      return { min, max, count };
    },
    entityRenderedMajorAxisEnd: (
      id: string,
    ): { x: number; y: number; z: number } | null => {
      const renderable = handle.spatialIndex.resolve(id);
      if (!renderable) return null;
      const verts = geometryToLineVertices(renderable.geometry);
      if (verts.length < 3) return null;
      const x = verts[0];
      const y = verts[1];
      const z = verts[2];
      if (
        typeof x !== "number" ||
        typeof y !== "number" ||
        typeof z !== "number"
      ) {
        return null;
      }
      return { x, y, z };
    },
    entityBounds: (id: string) => {
      // Opt 3 — bounds live intrinsically in the index and are
      // exposed via `getBounds(id)` so we no longer have to
      // intersect-and-scan to find them.
      //
      // #589 Codex round 1 — `spatialIndex.getBounds()` is, for the common
      // render path, in the offset-world frame `clientFromWorld`'s
      // internal `applyOffset` expects to STRIP, not the AutoCAD-world
      // frame its PUBLIC contract expects as input — feeding it
      // `unapplyOffset` first, then letting `clientFromWorld` `applyOffset`
      // it right back, is a no-op on the coordinate value (the two exactly
      // cancel) but routes it through the SAME camera-projection code path.
      //
      // #589 Codex round 5 — `entityBounds()`'s contract is "what does the
      // RENDERED pixel bbox look like", and a rendered pixel is ALWAYS
      // painted from offset-world/render-space geometry (that is the entire
      // point of `coordinateOrigin.ts`'s Float32-safety offset), so this
      // method must land on the correct render-space value before
      // projecting. It branches on `getBoundsFrame()` exactly like
      // `entityWorldBounds()`: a raw-world source is ALREADY AutoCAD-world,
      // so it passes straight to `clientFromWorld`; an offset-world source
      // still needs `unapplyOffset` first as above.
      //
      // Both production bulk-load branches now declare `"offset-world"`
      // (the cache-direct path used to declare `"raw-world"`; the raw box
      // is converted at `state/spatialBuilder.ts`'s one seam instead), so
      // the raw-world arm is currently reached only by a caller that sets
      // the frame itself. The branch stays because the frame is a declared
      // per-index signal, not a constant, and reading it is free.
      // KNOWN GAP — PAPER-SPACE VIEWPORT FRAME (same root as
      // `entityIdAt`'s "KNOWN GAP 2" above, and equally pre-existing).
      // In layout mode the index also holds `modelSpaceEntities` that are
      // PAINTED through a paper-space viewport's own transform, not by
      // the main paper camera. Projecting those bounds with
      // `clientFromWorld` therefore yields where the geometry would sit
      // in paper coords, not the pixels the viewport actually draws it
      // at. Callers measuring geometry INSIDE a layout viewport get the
      // wrong rectangle; every current consumer measures model space, so
      // this is latent. Closing it requires the same VP-redirect state
      // `entityIdAt` needs — one follow-up slice for both.
      const bounds = handle.spatialIndex.getBounds(id);
      if (!bounds) return null;
      const isRawWorld =
        (handle.spatialIndex.getBoundsFrame?.() ?? "offset-world") ===
        "raw-world";
      const toAutocadWorld = (p: { x: number; y: number }) =>
        isRawWorld ? p : unapplyOffset({ x: p.x, y: p.y, z: 0 });
      const tl = projectionOrIdentity.clientFromWorld(
        toAutocadWorld({ x: bounds.minX, y: bounds.minY }),
      );
      const br = projectionOrIdentity.clientFromWorld(
        toAutocadWorld({ x: bounds.maxX, y: bounds.maxY }),
      );
      return {
        left: Math.min(tl.x, br.x),
        top: Math.min(tl.y, br.y),
        right: Math.max(tl.x, br.x),
        bottom: Math.max(tl.y, br.y),
      };
    },
    entityWorldBounds: (id: string) => {
      // Same intrinsic-index lookup as `entityBounds`, but returned as
      // AutoCAD-world (coordinateOrigin re-added) instead of projected to
      // client pixels — camera-invariant, matching this method's own
      // documented "raw AutoCAD-WORLD units" contract. `top = minY` /
      // `bottom = maxY` here (unlike `cameraWorldRect()`), since there is
      // no screen-space Y-flip without a camera projection to apply it.
      //
      // #589 Codex round 1 — `spatialIndex.getBounds()` is in offset-world
      // (post-`applyOffset`) for the common render path, so `unapplyOffset`
      // is required to genuinely deliver AutoCAD-world as documented;
      // returning it verbatim was off by `coordinateOrigin` whenever one
      // was locked.
      //
      // #589 Codex round 2 — unconditionally applying `unapplyOffset` was
      // then WRONG for the cache-direct (>`SHELL_ENTITY_PREVIEW_THRESHOLD`-
      // entity) path, whose spatialIndex stored raw/AutoCAD-world bounds at
      // the time — double-adding the origin there. That split is GONE: the
      // cache-direct branch now converts the raw `EntityMetadataIndex` box
      // at `state/spatialBuilder.ts`'s one seam and declares
      // `"offset-world"` like the legacy branch, so in production this
      // method always takes the `unapplyOffset` arm below.
      //
      // #589 Codex round 3 — a `spatialIndex.size()` HEURISTIC to guess
      // which path populated the index was itself wrong: cache-direct's
      // trigger is the DOCUMENT's total entity count, not the active
      // space's post-filter stub count, so a >threshold document with a
      // <=threshold-sized active space (multi-space documents, annotative
      // filtering) would be misclassified. Replaced with the EXPLICIT
      // `getBoundsFrame()` signal `SceneBulkLoad.ts` sets right after each
      // `bulkLoad()` call — no guessing. The signal is kept (and still read
      // here) because the frame is a declared per-index property: an index
      // this bridge is handed need not have been populated by
      // `SceneBulkLoad.ts` at all.
      //
      // This method's contract is "give the TRUE AutoCAD-world coordinate"
      // regardless of render path, so it uses that explicit signal rather
      // than assuming one frame.
      //
      // SCOPE of that promise — it is about the OFFSET frame, not about
      // paper-space viewports. `getBoundsFrame()` resolves offset-world vs
      // raw-world; it says nothing about WHICH space the entry was indexed
      // in. In layout mode `SceneBulkLoad.ts` also appends
      // `modelSpaceEntities` as the active viewport maps them, so for an
      // entity displayed THROUGH a viewport the value below is that
      // viewport-mapped paper box, not model-space WCS. Same latent
      // viewport gap `entityIdAt` ("KNOWN GAP 2") and `entityBounds`
      // carry, and it closes with the same follow-up slice; in plain
      // model space (every current consumer) the two frames coincide.
      const bounds = handle.spatialIndex.getBounds(id);
      if (!bounds) return null;
      if ((handle.spatialIndex.getBoundsFrame?.() ?? "offset-world") === "raw-world") {
        return {
          left: bounds.minX,
          top: bounds.minY,
          right: bounds.maxX,
          bottom: bounds.maxY,
        };
      }
      const min = unapplyOffset({ x: bounds.minX, y: bounds.minY, z: 0 });
      const max = unapplyOffset({ x: bounds.maxX, y: bounds.maxY, z: 0 });
      return {
        left: min.x,
        top: min.y,
        right: max.x,
        bottom: max.y,
      };
    },
    entityVisible: (id: string): boolean => store.isEntityVisible(id),
    selectionIds: (): string[] => store.getSelectionIds(),
    setSelection: (ids: string[]): void => {
      // PR-C: programmatic test entry point — defers to the store
      // setter if wired, otherwise no-ops (PR-A stub).
      store.setSelection?.(ids);
    },
    cameraWorldRect: () => projectionOrIdentity.cameraWorldRect(),
    cameraZoom: () => projectionOrIdentity.cameraZoom(),
    cameraPosition: () => projectionOrIdentity.cameraPosition(),
    snapshotPng: async (): Promise<string> => {
      const r = handle.renderer as { domElement?: HTMLCanvasElement } | undefined;
      const canvas = r?.domElement;
      if (!canvas) {
        return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
      }
      return canvas.toDataURL("image/png");
    },
    waitForFrame: (): Promise<void> => {
      return new Promise<void>((resolve) => {
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => resolve());
        } else {
          setTimeout(resolve, 16);
        }
      });
    },
    preloadFontAtlas: async (fontName: string): Promise<void> => {
      // Drive the loader and discard the SdfAtlas value — tests only
      // care about the side-effect (cache populated, texture uploaded).
      // Errors propagate as `MissingAtlasError` so specs can assert on
      // both happy + missing-atlas paths.
      await loadSdfAtlas(fontName);
    },
  };

  // Attach. Cast intentionally permissive — we own the property.
  (window as unknown as Record<string, unknown>)[BRIDGE_PROPERTY_NAME] = api;

  return () => {
    if (typeof window === "undefined") return;
    const slot = (window as unknown as Record<string, unknown>)[
      BRIDGE_PROPERTY_NAME
    ];
    // Only detach if our handle is still the current one — otherwise
    // a sibling mount has taken over and we'd be clobbering it.
    if (slot === api) {
      delete (window as unknown as Record<string, unknown>)[
        BRIDGE_PROPERTY_NAME
      ];
    }
  };
}

/** Test-only: read the currently-installed bridge (or null). */
export function getInstalledSceneTestBridgeForTest(): SceneTestApi | null {
  if (typeof window === "undefined") return null;
  const slot = (window as unknown as Record<string, unknown>)[
    BRIDGE_PROPERTY_NAME
  ];
  return (slot as SceneTestApi | undefined) ?? null;
}
